import { Bell, Compass, MessageSquareHeart, Phone, Shirt, ShoppingBag, Sparkles, UtensilsCrossed, Wifi } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ServiceTile, type TileTint } from '@/components/common/ServiceTile';
import { OfferCarousel } from '@/components/common/OfferCarousel';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { toast } from 'sonner';

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}

/**
 * The home screen from the reference design: hotel name, a welcome hero, the
 * "How can we help you?" grid, and the offers carousel beneath it when the
 * hotel has any.
 *
 * Call Reception and Feedback are always there. Housekeeping, Restaurant and
 * Laundry appear only when the hotel has switched them on — read from the
 * flags the catalogue callable returns, which come from the hotel document.
 * While the catalogue loads, the three conditional slots are skeletons rather
 * than absent, so the grid does not jump when they arrive.
 */
export function HomePage() {
  const { session, services, servicesPhase, servicesError, reloadServices } = useReadyGuest();
  const flags = services?.flags;

  const tiles: Array<{
    to: string;
    icon: typeof Phone;
    title: string;
    subtitle: string;
    tint: TileTint;
    show: boolean;
  }> = [
    { to: '/call', icon: Phone, title: 'Call Reception', subtitle: 'Available 24/7', tint: 'green', show: true },
    {
      to: '/housekeeping',
      icon: Sparkles,
      title: 'Housekeeping',
      subtitle: 'Towels, water & more',
      tint: 'purple',
      show: flags?.houseKeepingService === true,
    },
    {
      to: '/restaurant',
      icon: UtensilsCrossed,
      title: 'Restaurant',
      subtitle: 'Order to your room',
      tint: 'orange',
      show: flags?.restaurantService === true,
    },
    {
      to: '/laundry',
      icon: Shirt,
      title: 'Laundry',
      subtitle: 'Wash, dry clean & iron',
      tint: 'blue',
      show: flags?.laundryService === true,
    },
    {
      to: '/store',
      icon: ShoppingBag,
      title: 'Hotel Store',
      subtitle: 'Toiletries & souvenirs',
      tint: 'orange',
      show: flags?.storeService === true,
    },
    {
      to: '/wifi',
      icon: Wifi,
      title: 'Wi-Fi Details',
      subtitle: 'Network & password',
      tint: 'teal',
      show: flags?.wifiService === true,
    },
    {
      to: '/explore',
      icon: Compass,
      title: 'Places to Explore',
      subtitle: 'Sights & tours nearby',
      tint: 'blue',
      show: flags?.placesToExploreService === true,
    },
    { to: '/feedback', icon: MessageSquareHeart, title: 'Feedback', subtitle: 'Tell us how we did', tint: 'pink', show: true },
  ];

  const loadingFlags = servicesPhase === 'loading' || servicesPhase === 'idle';

  return (
    <AppShell>
      <header className="bg-card flex items-center justify-between px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="min-w-0">
          <p className="text-muted-foreground text-[11px] font-semibold tracking-widest uppercase">Welcome to</p>
          <h1 className="truncate text-lg font-bold leading-tight">{session.hotelName}</h1>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label="Notifications"
          onClick={() => toast('Updates on your requests appear under Requests.')}
        >
          <Bell className="size-5" />
        </Button>
      </header>

      <section className="relative overflow-hidden bg-[radial-gradient(120%_120%_at_0%_0%,oklch(0.42_0.16_262)_0%,oklch(0.25_0.08_262)_60%,oklch(0.2_0.05_262)_100%)] px-5 pt-8 pb-10 text-white">
        {/* The booking's lead guest. Falls back to "Guest" for a booking with
            no name on it, and for a session registered before the field
            existed — those refresh on the next load. */}
        <p className="text-sm text-white/80">
          {greeting()}, {session.leadGuestName ?? 'Guest'} 👋
        </p>
        <h2 className="mt-1 text-2xl font-bold">Room {session.roomNumber}</h2>
        <p className="mt-1 text-sm text-white/80">We’re delighted to have you with us.</p>
      </section>

      <section className="bg-background -mt-4 flex-1 rounded-t-3xl px-4 pt-5 pb-6">
        <h3 className="text-base font-semibold">How can we help you?</h3>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {tiles.map((tile) =>
            tile.show ? (
              <ServiceTile key={tile.to} {...tile} />
            ) : loadingFlags && tile.to !== '/call' && tile.to !== '/feedback' ? (
              <Skeleton key={tile.to} className="h-[7.25rem] rounded-2xl" />
            ) : null,
          )}
        </div>

        {servicesPhase === 'error' && (
          <div className="bg-destructive/10 text-destructive mt-4 flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm" role="alert">
            <span>{servicesError ?? 'Some services could not be loaded.'}</span>
            <Button size="sm" variant="outline" onClick={reloadServices}>
              Retry
            </Button>
          </div>
        )}

        {/* Offers below the grid: the services are what the guest came for,
            the offers are the hotel's word once they have found them. Nothing
            renders while the catalogue loads or when there are none, so a
            hotel without offers sees no gap. */}
        {services?.offers?.length ? (
          <>
            <h3 className="mt-6 text-base font-semibold">Offers for you</h3>
            <OfferCarousel offers={services.offers} className="mt-3" />
          </>
        ) : null}

        <p className="text-muted-foreground mt-8 text-center text-xs">
          Powered by{' '}
          <a
            href="https://roomservize.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline-offset-2 hover:underline"
          >
            Roomservize.com
          </a>
        </p>
      </section>
    </AppShell>
  );
}
